import { forwardRef, isValidElement, type ComponentRef } from "react";

import { useWindowDimensions } from "@luce/core";

import { cn, platformProps } from "../../lib";
import { markPendingAware } from "../../lib/pending";
import { Pressable } from "../../primitives";
import { Icon } from "../icons";
import { Spinner } from "../spinner";
import { Typography } from "../typography";
import { ButtonAsChild } from "./button-as-child";
import { buttonVariants } from "./button.variants";
import {
  buttonHitSlop,
  buttonIconColor,
  buttonIconPx,
  buttonIconStateClass,
  buttonSpinnerColorFor,
  buttonSpinnerSize,
  buttonTypographyColor,
  buttonTypographyStateClass,
  buttonTypographyVariant,
  getButtonAccessibilityLabel,
  getButtonAccessibilityState,
} from "./lib";
import type { ButtonProps } from "./types";

export const Button = forwardRef<ComponentRef<typeof Pressable>, ButtonProps>(
  (
    {
      label,
      children,
      asChild = false,
      leadingIcon,
      trailingIcon,
      leadingIconWeight,
      trailingIconWeight,
      variant = "primary",
      size = "md",
      fullWidth = false,
      loading = false,
      className,
      testID,
      ...pressableProps
    },
    ref,
  ) => {
    const {
      onClick,
      disabled = false,
      accessibilityLabel,
      accessibilityState,
      hitSlop = buttonHitSlop[size],
      pending = false,
      ...restPressableProps
    } = pressableProps;

    const isDisabled = disabled;
    const { isDesktop } = useWindowDimensions();
    const iconPx = buttonIconPx[size][isDesktop ? "desktop" : "mobile"];

    const accessibilityProps = {
      accessibilityLabel,
      accessibilityState,
      label,
      loading,
      isDisabled,
      asChild,
    };

    const resolvedAccessibilityLabel = getButtonAccessibilityLabel(accessibilityProps);
    const mergedAccessibilityState = getButtonAccessibilityState(accessibilityProps);

    const buttonClassName = cn(buttonVariants({ variant, size, fullWidth, loading }), className);

    // Shared pressable props
    const sharedProps = {
      ...restPressableProps,
      ...platformProps({ testID, slot: "button" }),
      accessibilityRole: "button" as const,
      accessibilityLabel: resolvedAccessibilityLabel,
      accessibilityState: mergedAccessibilityState,
      disabled: isDisabled,
      // Loading ignores every activation, not only onClick; a caller's
      // `pending={false}` can't undo it
      pending: pending || loading,
      hitSlop,
      onClick,
      className: buttonClassName,
      "aria-busy": loading,
    };

    // ==========================================================================
    // asChild mode - merge props into child element
    // ==========================================================================
    if (asChild) {
      if (!isValidElement(children)) {
        console.warn("Button with asChild requires a valid React element as children");
        return null;
      }

      return (
        <ButtonAsChild ref={ref} {...sharedProps}>
          {children}
        </ButtonAsChild>
      );
    }

    // ==========================================================================
    // Render button content (label with optional icons)
    // ==========================================================================
    const iconColorClass = buttonIconColor[variant][isDisabled ? "disabled" : "default"];
    const iconStateClass = !isDisabled ? buttonIconStateClass[variant] : undefined;

    const renderContent = () => {
      if (loading) {
        return (
          <Spinner
            size={buttonSpinnerSize[size]}
            color={buttonSpinnerColorFor(variant, isDisabled)}
            decorative
            className="shrink-0"
          />
        );
      }

      return (
        <>
          {leadingIcon && (
            <Icon
              name={leadingIcon}
              size={iconPx}
              weight={leadingIconWeight}
              className={cn("shrink-0", iconColorClass, iconStateClass)}
            />
          )}
          {label && (
            <Typography
              variant={buttonTypographyVariant[size]}
              color={buttonTypographyColor[variant][isDisabled ? "disabled" : "default"]}
              whiteSpace="nowrap"
              className={cn(
                "max-w-full shrink truncate",
                !isDisabled && buttonTypographyStateClass[variant],
              )}
            >
              {label}
            </Typography>
          )}
          {trailingIcon && (
            <Icon
              name={trailingIcon}
              size={iconPx}
              weight={trailingIconWeight}
              className={cn("shrink-0", iconColorClass, iconStateClass)}
            />
          )}
        </>
      );
    };

    return (
      <Pressable ref={ref} {...sharedProps}>
        {renderContent()}
      </Pressable>
    );
  },
);

Button.displayName = "Button";
markPendingAware(Button);
